import React from "react";
import {
  Box,
  Card,
  CardContent,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import {
  formatDateTime,
  formatDuration,
} from "../../utils/locationAnalytics";

/**
 * A vertical list of detected stops with click-through to jump the map
 * playhead. Designed as the "investigator's index" — chronological, dense,
 * each entry collapsible if needed.
 */
export const MovementStopList = ({
  stops = [],
  onSelectStop,
  activeStopIndex = null,
  getLabel,
}) => {
  return (
    <Card>
      <CardContent sx={{ py: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: "#5f6877",
              letterSpacing: "0.12em",
              fontWeight: 600,
              fontSize: "10.5px",
            }}
          >
            Identifierade stopp
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Outfit", "DM Sans", sans-serif',
              fontWeight: 700,
              color: "#10324a",
              fontSize: "14px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {stops.length}
          </Typography>
        </Box>

        {stops.length === 0 ? (
          <Typography
            sx={{
              color: "#7a8294",
              fontSize: 13,
              lineHeight: 1.55,
              mt: 1,
            }}
          >
            Inga stopp identifierade i detta tidsspann. Ett stopp räknas som
            minst 2 minuter inom en 35-meters-radie.
          </Typography>
        ) : (
          <List
            sx={{
              maxHeight: 420,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {stops.map((stop, idx) => {
              const isActive = activeStopIndex === idx;
              const rawLabel =
                typeof getLabel === "function"
                  ? getLabel(stop.center.latitude, stop.center.longitude)
                  : undefined;
              const placeLabel =
                typeof rawLabel === "string" && rawLabel.length > 0
                  ? rawLabel
                  : rawLabel === null
                  ? "Okänd plats"
                  : "Hämtar adress…";
              return (
                <ListItemButton
                  key={`${stop.startAt}-${idx}`}
                  onClick={() => onSelectStop?.(stop, idx)}
                  sx={{
                    borderRadius: "10px",
                    mb: 0.8,
                    border: isActive
                      ? "1px solid #8a1125"
                      : "1px solid #e2e5ea",
                    background: isActive
                      ? "rgba(138,17,37,0.06)"
                      : "#ffffff",
                    transition:
                      "background 160ms ease, border-color 160ms ease",
                    "&:hover": {
                      background: "rgba(16,50,74,0.04)",
                      borderColor: "#10324a",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "radial-gradient(circle at 35% 35%, #ffd770, #d97706)",
                        color: "#1c1406",
                        fontFamily: '"Outfit", "DM Sans", sans-serif',
                        fontWeight: 700,
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: '"DM Sans", system-ui, sans-serif',
                          fontWeight: 600,
                          color: "#10324a",
                          fontSize: 13.5,
                          lineHeight: 1.3,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatDuration(stop.dwellMs)}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#7a8294",
                          fontSize: 11.5,
                          fontVariantNumeric: "tabular-nums",
                          mt: 0.2,
                        }}
                      >
                        {formatDateTime(new Date(stop.startAt).toISOString())}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#5f6877",
                          fontSize: 11.5,
                          fontFamily: '"DM Sans", system-ui, sans-serif',
                          fontStyle:
                            placeLabel === "Hämtar adress…"
                              ? "italic"
                              : "normal",
                          mt: 0.3,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={placeLabel}
                      >
                        <PlaceRoundedIcon
                          sx={{ fontSize: 12, flexShrink: 0 }}
                        />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {placeLabel}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default MovementStopList;
